import { httpClient } from '../utils/httpClient';
import type { UIConfiguration } from '@/types/Entity';

export interface EntityTypeConfiguration {
  entity_type: string;
  display_name: string;
  description?: string;
  entity_schema?: JSONSchema;
  ui_schema?: UISchemaElement; // ✅ NEW: UI Schema support
  ui_schema_edit?: UISchemaElement;
  // Raw UI preferences bag (source of truth for prefs)
  ui_configuration?: UIConfiguration;
  active: boolean;
}

export interface JSONSchema {
  type: string;
  properties?: Record<string, JSONSchemaProperty>;
  required?: string[];
  title?: string;
  description?: string;
}

export interface JSONSchemaProperty {
  type: string;
  title?: string;
  description?: string;
  enum?: string[];
  minimum?: number;
  maximum?: number;
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  default?: unknown;
}

// ✅ NEW: UI Schema types for JSON Forms
export interface UISchemaElement {
  type: string;
  label?: string;
  scope?: string;
  elements?: UISchemaElement[];
  options?: Record<string, unknown>;
}

// Navigation menu types (structure defined in code, see web/src/config/defaultNavigation.ts)
export interface MenuLeafConfig {
  key: string;
  label: string;
  entity_type: string;
  icon?: string;
  query?: Record<string, string>;
  route?: string;
  adminOnly?: boolean;
  viewerHidden?: boolean;
}

export interface MenuGroupDef {
  display_name: string;
  icon?: string;
  entity_order?: string[]; // list of entity_type keys in order
  category_leaves?: MenuLeafConfig[];
}

export interface MenuConfig {
  groups: Record<string, MenuGroupDef>;
  group_order?: string[];
  ungrouped_entity_order?: string[];
  placeholder_routes?: Array<{ key: string; label: string; icon?: string; route?: string }>;
}

export class ConfigService {
  async getEntityTypes(): Promise<EntityTypeConfiguration[]> {
    // Pull active entity types with UI configuration; client builds nav from central config
    return httpClient.get<EntityTypeConfiguration[]>(
      `/api/v1/entity_types?active=eq.true&select=entity_type,display_name,description,ui_configuration,active`,
    );
  }

  async getEntityTypeConfig(entityType: string): Promise<EntityTypeConfiguration> {
    const response = await httpClient.get<EntityTypeConfiguration[]>(
      `/api/v1/entity_types?entity_type=eq.${entityType}&select=*`,
    );

    if (!response || response.length === 0) {
      // Return a minimal fallback so the page renders with an empty grid
      return {
        entity_type: entityType,
        display_name: entityType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
        active: true,
      };
    }

    return response[0]!;
  }

  /**
   * Get JSON Schema for entity type (for form generation)
   */
  async getEntitySchema(entityType: string): Promise<JSONSchema> {
    const response = await httpClient.get<Array<{ entity_schema: JSONSchema | null }>>(
      `/api/v1/entity_types?entity_type=eq.${entityType}&select=entity_schema`,
    );

    const row = response?.[0];
    if (!row || !row.entity_schema) {
      throw new Error(`Entity schema not found: ${entityType}`);
    }

    return row.entity_schema;
  }

  /**
   * ✅ NEW: Get UI Schema for entity type (for form layout)
   */
  async getUISchema(entityType: string): Promise<UISchemaElement | null> {
    const response = await httpClient.get<Array<{ ui_schema: UISchemaElement | null }>>(
      `/api/v1/entity_types?entity_type=eq.${entityType}&select=ui_schema`,
    );

    const row = response?.[0];
    if (!row) {
      return null;
    }

    return row.ui_schema || null;
  }

  /**
   * ✅ ENHANCED: Get complete entity type configuration including both schemas
   */
  async getEntityTypeWithSchemas(entityType: string): Promise<EntityTypeConfiguration> {
    const response = await httpClient.get<EntityTypeConfiguration[]>(
      `/api/v1/entity_types?entity_type=eq.${entityType}&select=entity_type,display_name,description,entity_schema,ui_schema,ui_schema_edit,ui_configuration,active`,
    );

    if (!response || response.length === 0) {
      throw new Error(`Entity type configuration not found: ${entityType}`);
    }

    return response[0]!;
  }

  /**
   * Get complete entity type configuration including schema (backward compatibility)
   */
  async getEntityTypeWithSchema(entityType: string): Promise<EntityTypeConfiguration> {
    return this.getEntityTypeWithSchemas(entityType);
  }

}

// Singleton instance
export const configService = new ConfigService();
